import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import ChartFrame from "@/components/ChartFrame";
import { Loader2, Trash2, Gauge, BookOpenText } from "lucide-react";
import { TOOLTIP_STYLE, TICK_MINI, DOT_BLUE, DOT_GREEN, INIT_DIM_MD } from "@/lib/chartStyles";

const DOMAIN_0_100 = [0, 100];

function FluencyTrends({ trend }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">WCPM trend</p>
        <ChartFrame className="h-24 -ml-2">
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={INIT_DIM_MD}>
            <LineChart data={trend}>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <XAxis dataKey="date" tick={TICK_MINI} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Line type="monotone" dataKey="wcpm" stroke="#6C8EBF" strokeWidth={2.5} dot={DOT_BLUE} />
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">Accuracy trend (%)</p>
        <ChartFrame className="h-24 -ml-2">
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={INIT_DIM_MD}>
            <LineChart data={trend}>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <XAxis dataKey="date" tick={TICK_MINI} axisLine={false} tickLine={false} />
              <YAxis domain={DOMAIN_0_100} hide />
              <Line type="monotone" dataKey="accuracy" stroke="#5d8a73" strokeWidth={2.5} dot={DOT_GREEN} />
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>
      </div>
    </div>
  );
}

function FluencyHistory({ records, onDelete }) {
  return (
    <div className="space-y-2 mt-4">
      {[...records].reverse().map((r) => (
        <div key={r.id} data-testid={`reading-row-${r.id}`} className="flex items-center gap-3 text-sm border-t border-slate-50 pt-2 first:border-0">
          <span className="text-slate-400 w-20 shrink-0">{new Date(r.date).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "2-digit" })}</span>
          <div className="flex-1 flex flex-wrap gap-x-4">
            <span className="text-slate-600"><span className="text-slate-400">WCPM:</span> <b>{r.wcpm}</b></span>
            <span className="text-slate-600"><span className="text-slate-400">Accuracy:</span> <b>{r.accuracy}%</b></span>
            <span className="text-slate-600"><span className="text-slate-400">Read:</span> {r.words_correct}/{r.total_words} in {r.seconds}s</span>
            {r.passage && <span className="text-slate-400 italic">{r.passage}</span>}
          </div>
          <button data-testid={`delete-reading-${r.id}`} onClick={() => onDelete(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
}

export default function ReadingFluency({ studentId }) {
  const [records, setRecords] = useState(null);
  const [form, setForm] = useState({ passage: "", total_words: "", errors: "", seconds: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/reading-records?student_id=${studentId}`).then((r) => setRecords(r.data)).catch(() => setRecords([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [studentId]);

  const tw = parseInt(form.total_words) || 0;
  const errs = parseInt(form.errors) || 0;
  const secs = parseInt(form.seconds) || 0;
  const correct = Math.max(tw - errs, 0);
  const previewAcc = tw > 0 ? Math.round((correct / tw) * 100) : 0;
  const previewWcpm = secs > 0 ? Math.round(correct / (secs / 60)) : 0;

  const save = async (e) => {
    e.preventDefault();
    if (tw <= 0 || secs <= 0) { toast.error("Enter words read and time."); return; }
    setSaving(true);
    try {
      await api.post("/reading-records", {
        student_id: studentId, passage: form.passage,
        total_words: tw, errors: errs, seconds: secs, notes: form.notes,
      });
      toast.success(`Reading run saved · ${previewWcpm} WCPM · ${previewAcc}%`);
      setForm({ passage: "", total_words: "", errors: "", seconds: "", notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/reading-records/${id}`); load(); };

  const trend = (records || []).map((r) => ({
    date: new Date(r.date).toLocaleDateString("en-IE", { day: "numeric", month: "short" }),
    wcpm: r.wcpm, accuracy: r.accuracy,
  }));

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
      <h3 className="font-['Outfit'] text-lg font-bold text-slate-800 flex items-center gap-2 mb-1">
        <Gauge size={20} className="text-[#6C8EBF]" /> Reading fluency
      </h3>
      <p className="text-sm text-slate-400 mb-4">Time a short reading run to track words-correct-per-minute (WCPM) and accuracy.</p>

      <form onSubmit={save} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block lg:col-span-4">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Passage / book (optional)</span>
          <input data-testid="reading-passage-input" value={form.passage} onChange={(e) => setForm({ ...form, passage: e.target.value })} placeholder="e.g. PM Reader L5"
            className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white" />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Words read</span>
          <input type="number" min="1" required data-testid="reading-total-input" value={form.total_words} onChange={(e) => setForm({ ...form, total_words: e.target.value })}
            className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white" />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Errors</span>
          <input type="number" min="0" data-testid="reading-errors-input" value={form.errors} onChange={(e) => setForm({ ...form, errors: e.target.value })}
            className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white" />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Time (seconds)</span>
          <input type="number" min="1" required data-testid="reading-seconds-input" value={form.seconds} onChange={(e) => setForm({ ...form, seconds: e.target.value })}
            className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white" />
        </label>
        <div className="flex items-end">
          <button data-testid="save-reading-btn" disabled={saving}
            className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-xl py-2.5 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <BookOpenText size={16} />} Save run
          </button>
        </div>
      </form>

      {(tw > 0 && secs > 0) && (
        <p className="mt-3 text-sm text-slate-500" data-testid="reading-preview">
          Preview: <b className="text-slate-800">{previewWcpm} WCPM</b> · <b className="text-slate-800">{previewAcc}% accuracy</b>
        </p>
      )}

      {records && records.length > 0 && (
        <>
          {records.length > 1 && <FluencyTrends trend={trend} />}
          <FluencyHistory records={records} onDelete={remove} />
        </>
      )}
    </div>
  );
}
